/**
 * TrialDetailScreen Component
 *
 * Shows the current Elite 4 member's details (Pokemon stats, moves)
 * and allows player to select a Hall of Fame Pokemon to battle with.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Swords,
  Target,
  X,
  Check,
  AlertTriangle,
  Zap,
  Clock
} from 'lucide-react';
import { useGame } from '../contexts/GameContext';
import { useInventory } from '../contexts/InventoryContext';
import { fetchTrialData, getTrialRegions, getElite4Member } from '../data/elite4Trials';
import { TYPE_COLORS, TypeBadge, TypeIcon, mapToGameType } from '../components/TypeIcon';
import { getTrainerFaceConfig } from '../constants/trainerImages';
import { MOVES } from '../shared/gameData';
import {
  generatePokemonSprite,
  getGradeColor,
  StatIcon,
  PokemonSprite,
  getBaseSpecies
} from '../utils/gameUtils';
import StatRadarChart from '../components/StatRadarChart';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

// Normalize move name to match MOVES keys (e.g., "Ice Beam" -> "IceBeam")
const normalizeMoveKey = (moveName) => {
  if (!moveName) return moveName;
  // Remove spaces and hyphens, keep PascalCase
  return moveName.replace(/[\s-]/g, '');
};

// Move display component
const MoveCard = ({ moveName, pokemon }) => {
  const moveKey = normalizeMoveKey(moveName);
  const move = MOVES?.[moveKey];
  const moveType = move?.type || pokemon?.primaryType || 'Normal';
  const typeColor = TYPE_COLORS[moveType] || '#A8A878';

  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border-l-4"
      style={{ borderLeftColor: typeColor }}
    >
      <div className="flex items-center gap-2">
        <TypeBadge type={moveType} size={14} />
        <span className="font-semibold text-pocket-text text-sm">{moveName}</span>
      </div>
      {move && (
        <div className="flex items-center gap-3 text-xs text-pocket-text-light">
          <span className="flex items-center gap-0.5" title="Damage"><Swords size={12} className="text-orange-500" />{move.damage}</span>
          <span className="flex items-center gap-0.5" title="Stamina"><Zap size={12} className="text-yellow-500" />{move.stamina}</span>
          <span className="flex items-center gap-0.5" title="Cooldown"><Clock size={12} className="text-pink-600" />{move.cooldown}</span>
        </div>
      )}
    </div>
  );
};

// Pokemon selection card for Hall of Fame
const PokemonSelectCard = ({ pokemon, isUsed, isSelected, onSelect }) => {
  const grade = pokemon.grade;
  const totalStats = Object.values(pokemon.stats || {}).reduce((sum, val) => sum + (val || 0), 0);

  return (
    <motion.button
      whileHover={!isUsed ? { scale: 1.02 } : {}}
      whileTap={!isUsed ? { scale: 0.98 } : {}}
      onClick={() => !isUsed && onSelect(pokemon)}
      disabled={isUsed}
      className={`relative rounded-xl p-3 text-left transition-all ${
        isUsed
          ? 'opacity-40 cursor-not-allowed bg-gray-100'
          : isSelected
          ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-400 shadow-lg'
          : 'bg-white border-2 border-transparent hover:border-gray-200 shadow-card'
      }`}
    >
      {/* Used overlay */}
      {isUsed && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-500/20 rounded-xl">
          <span className="text-xs font-bold text-gray-600 bg-white px-2 py-0.5 rounded">USED</span>
        </div>
      )}

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow">
          <Check size={14} className="text-white" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 flex-shrink-0">
          <PokemonSprite pokemonName={pokemon.name} size={48} useScale={false} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-pocket-text text-sm truncate">{pokemon.name}</h4>
          <div className="flex items-center gap-2 mt-0.5">
            <TypeBadge type={pokemon.type} size={12} />
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
              style={{ backgroundColor: getGradeColor(grade) }}
            >
              {grade}
            </span>
          </div>
          <p className="text-[10px] text-pocket-text-light mt-0.5">{totalStats} Total Stats</p>
        </div>
      </div>
    </motion.button>
  );
};

const TrialDetailScreen = () => {
  const { setGameState, trialState, setTrialState } = useGame();
  const { trainedPokemon } = useInventory();

  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [showPokemonSelect, setShowPokemonSelect] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [trialRegions, setTrialRegions] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch trial data on mount
  useEffect(() => {
    const loadData = async () => {
      const data = await fetchTrialData();
      if (data) {
        setTrialRegions(data.regions || {});
      }
      setDataLoaded(true);
    };
    loadData();
  }, []);

  // Get current region and member
  const region = trialState?.regionKey ? trialRegions[trialState.regionKey] : null;
  const currentMember = trialState?.regionKey && region
    ? region.members?.[trialState.currentMemberIndex || 0]
    : null;

  // Get enemy stats (base stats are already ~3200 total for UU+ grade)
  const scaledStats = useMemo(() => {
    if (!currentMember?.pokemon?.stats) return null;
    return currentMember.pokemon.stats;
  }, [currentMember]);

  // Filter available Pokemon (species not used in this trial)
  // usedSpecies stores base species names (e.g., "Charmander" for Charmander/Charmeleon/Charizard)
  const availablePokemon = useMemo(() => {
    const usedSpecies = trialState?.usedSpecies || [];
    let available = trainedPokemon.filter(p => {
      const baseSpecies = getBaseSpecies(p.name);
      return !usedSpecies.includes(baseSpecies);
    });

    // Apply type filter
    if (filterType !== 'all') {
      available = available.filter(p => p.type === filterType);
    }

    return available;
  }, [trainedPokemon, trialState?.usedSpecies, filterType]);

  // Helper to check if a specific Pokemon's species is used
  const isSpeciesUsed = (pokemonName) => {
    const usedSpecies = trialState?.usedSpecies || [];
    const baseSpecies = getBaseSpecies(pokemonName);
    return usedSpecies.includes(baseSpecies);
  };

  // Get unique types from trained Pokemon for filter
  const availableTypes = useMemo(() => {
    const types = new Set(trainedPokemon.map(p => p.type));
    return Array.from(types).sort();
  }, [trainedPokemon]);

  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-pocket-text-light">Loading trial data...</p>
        </div>
      </div>
    );
  }

  if (!region || !currentMember) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <p className="text-pocket-text">Invalid trial state</p>
          <button
            onClick={() => setGameState('trials')}
            className="mt-4 pocket-btn-primary px-6 py-2"
          >
            Back to Trials
          </button>
        </div>
      </div>
    );
  }

  const memberIndex = trialState.currentMemberIndex || 0;
  const gameType = mapToGameType(currentMember.type);
  const typeColor = TYPE_COLORS[gameType] || region.color;
  const totalScaledStats = Object.values(scaledStats || {}).reduce((sum, val) => sum + val, 0);

  const handleStartBattle = () => {
    if (!selectedPokemon) return;

    // Update trial state with selected Pokemon and start battle
    setTrialState({
      ...trialState,
      inProgress: true,
      currentPokemon: selectedPokemon,
      opponent: {
        ...currentMember,
        pokemon: {
          ...currentMember.pokemon,
          stats: scaledStats
        }
      }
    });

    setGameState('trialBattle');
  };

  return (
    <div className="min-h-screen bg-pocket-bg">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 shadow-lg"
        style={{ background: `linear-gradient(135deg, ${typeColor}, ${typeColor}CC)` }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setGameState('trials')}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="text-center">
            <span className="font-bold text-white">{region.name} Elite 4</span>
            <p className="text-white/70 text-xs">Battle {memberIndex + 1} of 4</p>
          </div>
          {/* Progress indicators */}
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3].map((idx) => (
              <div
                key={idx}
                className={`w-3 h-3 rounded-full ${
                  idx < memberIndex
                    ? 'bg-amber-400'
                    : idx === memberIndex
                    ? 'bg-white'
                    : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.header>

      <motion.main
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="p-4 pb-8 max-w-lg mx-auto"
      >
        {/* Elite 4 Member Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-card overflow-hidden mb-4"
        >
          {/* Member header */}
          <div
            className="p-4"
            style={{ background: `linear-gradient(135deg, ${typeColor}20, ${typeColor}40)` }}
          >
            <div className="flex items-center gap-4">
              {(() => {
                // Use trainer face config for circular portraits (same as hangout events)
                const faceConfig = getTrainerFaceConfig(currentMember.name);
                return (
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden shadow-lg border-3"
                    style={{ borderColor: typeColor }}
                  >
                    {currentMember.sprite ? (
                      <img
                        src={currentMember.sprite}
                        alt={currentMember.name}
                        className="w-full h-full object-cover"
                        style={{
                          transform: `scale(${faceConfig.scale}) translate(${faceConfig.offsetX}%, ${faceConfig.offsetY}%)`,
                          transformOrigin: 'top center'
                        }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-white text-2xl font-bold"
                        style={{ backgroundColor: typeColor }}
                      >
                        {currentMember.name[0]}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <h2 className="text-xl font-bold text-pocket-text">{currentMember.name}</h2>
                <p className="text-pocket-text-light text-sm">{currentMember.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <TypeBadge type={mapToGameType(currentMember.type)} size={16} />
                  <span className="text-xs text-pocket-text-light">Specialist</span>
                </div>
              </div>
            </div>
            {currentMember.quote && (
              <p className="mt-3 text-sm italic text-pocket-text-light border-l-2 pl-3" style={{ borderColor: typeColor }}>
                "{currentMember.quote}"
              </p>
            )}
          </div>

          {/* Pokemon info */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Swords size={16} className="text-red-500" />
                <span className="font-bold text-pocket-text">Battle Pokemon</span>
              </div>
              <span className="text-xs text-pocket-text-light bg-gray-100 px-2 py-1 rounded">
                {totalScaledStats} Total Stats
              </span>
            </div>

            {/* Pokemon display */}
            <div className="bg-pocket-bg rounded-xl p-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16">
                  {generatePokemonSprite(currentMember.pokemon.primaryType, currentMember.pokemon.name, 64, false)}
                </div>
                <div>
                  <h3 className="font-bold text-lg text-pocket-text">{currentMember.pokemon.name}</h3>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={currentMember.pokemon.primaryType} size={16} />
                    {currentMember.pokemon.secondaryType && (
                      <TypeBadge type={currentMember.pokemon.secondaryType} size={16} />
                    )}
                  </div>
                </div>
              </div>

              {/* Stats radar chart */}
              <div className="flex justify-center mb-4">
                <StatRadarChart stats={scaledStats} size={140} color={typeColor} />
              </div>

              {/* Stats breakdown */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                  <StatIcon stat="HP" size={14} />
                  <span className="text-pocket-text-light">HP</span>
                  <span className="ml-auto font-bold">{scaledStats?.HP}</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                  <StatIcon stat="Attack" size={14} />
                  <span className="text-pocket-text-light">ATK</span>
                  <span className="ml-auto font-bold">{scaledStats?.Attack}</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                  <StatIcon stat="Defense" size={14} />
                  <span className="text-pocket-text-light">DEF</span>
                  <span className="ml-auto font-bold">{scaledStats?.Defense}</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                  <StatIcon stat="Instinct" size={14} />
                  <span className="text-pocket-text-light">INS</span>
                  <span className="ml-auto font-bold">{scaledStats?.Instinct}</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 col-span-2 justify-center">
                  <StatIcon stat="Speed" size={14} />
                  <span className="text-pocket-text-light">SPD</span>
                  <span className="ml-2 font-bold">{scaledStats?.Speed}</span>
                </div>
              </div>
            </div>

            {/* Moves */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} className="text-blue-500" />
                <span className="font-bold text-pocket-text text-sm">Moves ({currentMember.pokemon.moves.length})</span>
              </div>
              <div className="space-y-1.5">
                {currentMember.pokemon.moves.map((moveName, idx) => (
                  <MoveCard key={idx} moveName={moveName} pokemon={currentMember.pokemon} />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Selected Pokemon / Selection Button */}
        <motion.div variants={itemVariants} className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-pocket-text">Your Pokemon</span>
            {trialState?.usedSpecies?.length > 0 && (
              <span className="text-xs text-pocket-text-light">
                {trialState.usedSpecies.length} species already used
              </span>
            )}
          </div>

          {selectedPokemon ? (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border-2 border-amber-300 shadow-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16">
                  <PokemonSprite pokemonName={selectedPokemon.name} size={64} useScale={false} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-pocket-text">{selectedPokemon.name}</h3>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={selectedPokemon.type} size={16} />
                    <span
                      className="px-2 py-0.5 rounded text-xs font-bold text-white"
                      style={{ backgroundColor: getGradeColor(selectedPokemon.grade) }}
                    >
                      {selectedPokemon.grade}
                    </span>
                  </div>
                  <p className="text-xs text-pocket-text-light mt-1">
                    {Object.values(selectedPokemon.stats || {}).reduce((sum, val) => sum + val, 0)} Total Stats
                  </p>
                </div>
                <button
                  onClick={() => setShowPokemonSelect(true)}
                  className="px-3 py-2 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors"
                >
                  Change
                </button>
              </div>
            </motion.div>
          ) : (
            <button
              onClick={() => setShowPokemonSelect(true)}
              className="w-full bg-white rounded-2xl p-6 border-2 border-dashed border-gray-300 hover:border-amber-400 hover:bg-amber-50/50 transition-all"
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                  <Swords size={28} className="text-gray-400" />
                </div>
                <p className="font-semibold text-pocket-text">Select a Pokemon</p>
                <p className="text-sm text-pocket-text-light">
                  Choose from your Hall of Fame
                </p>
              </div>
            </button>
          )}
        </motion.div>

        {/* Battle Button */}
        <motion.div variants={itemVariants}>
          <button
            onClick={handleStartBattle}
            disabled={!selectedPokemon}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
              selectedPokemon
                ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Swords size={24} />
            Start Battle
          </button>
        </motion.div>
      </motion.main>

      {/* Pokemon Selection Modal */}
      <AnimatePresence>
        {showPokemonSelect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
            onClick={() => setShowPokemonSelect(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="font-bold text-pocket-text text-lg">Select Pokemon</h3>
                  <p className="text-xs text-pocket-text-light">
                    {availablePokemon.length} available
                  </p>
                </div>
                <button
                  onClick={() => setShowPokemonSelect(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-pocket-text-light" />
                </button>
              </div>

              {/* Type filter */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 overflow-x-auto">
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                    filterType === 'all'
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-pocket-text-light hover:bg-gray-200'
                  }`}
                >
                  All Types
                </button>
                {availableTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
                      filterType === type
                        ? 'text-white'
                        : 'bg-gray-100 text-pocket-text-light hover:bg-gray-200'
                    }`}
                    style={filterType === type ? { backgroundColor: TYPE_COLORS[type] || '#6B7280' } : {}}
                  >
                    <TypeIcon type={type} size={12} />
                    {type}
                  </button>
                ))}
              </div>

              {/* Pokemon list */}
              <div className="flex-1 overflow-y-auto p-4">
                {availablePokemon.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertTriangle size={48} className="text-amber-400 mx-auto mb-3" />
                    <p className="text-pocket-text font-semibold">No Pokemon Available</p>
                    <p className="text-pocket-text-light text-sm mt-1">
                      {filterType !== 'all'
                        ? 'Try a different type filter'
                        : 'All your Pokemon have been used in this trial'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {availablePokemon.map((pokemon) => {
                      // Check if this species (evolution chain) is already used
                      const isUsed = isSpeciesUsed(pokemon.name);
                      return (
                        <PokemonSelectCard
                          key={pokemon.id}
                          pokemon={pokemon}
                          isUsed={isUsed}
                          isSelected={selectedPokemon?.id === pokemon.id}
                          onSelect={(p) => {
                            setSelectedPokemon(p);
                            setShowPokemonSelect(false);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="px-5 py-4 border-t border-gray-100">
                <button
                  onClick={() => setShowPokemonSelect(false)}
                  className="w-full py-3 bg-gray-100 text-pocket-text rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrialDetailScreen;
